import { Alert, Box, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import { forwardRef } from 'react';
import type { ReactNode } from 'react';
import type { FieldErrors, FieldValues } from 'react-hook-form';
import { Navigate, useParams } from 'react-router-dom';
import type { EnrollmentCosts, EnrollmentDto } from '@app/shared';
import { PageHeader } from '../../components/PageHeader';
import { ErrorState, LoadingState } from '../../components/PageState';
import { formatCents } from '../../lib/format';
import { useEnrollment } from './api';
import { currentStep, customerName, serviceAddress, stepPath } from './labels';
import type { WizardStep } from './labels';

/** Pieces shared by the pages of the enrollment wizard. */

/**
 * Loads the enrollment of the route and keeps the user on a page that exists for it:
 * a complete enrollment has only its confirmation, and an incomplete one has no page beyond
 * the step it has reached. Earlier steps stay open so they can be corrected.
 */
export function WizardGate({
  step,
  title,
  children,
}: {
  step: WizardStep;
  title: string;
  children: (enrollment: EnrollmentDto) => ReactNode;
}) {
  const { id } = useParams<{ id: string }>();
  const enrollment = useEnrollment(id);

  if (enrollment.error)
    return (
      <Box>
        <PageHeader title={title} code="PC1142" />
        <ErrorState error={enrollment.error} onRetry={() => void enrollment.refetch()} />
      </Box>
    );
  if (!enrollment.data)
    return (
      <Box>
        <PageHeader title={title} code="PC1142" />
        <LoadingState />
      </Box>
    );

  const reached = currentStep(enrollment.data);
  const allowed = reached === 'done' ? step === 'done' : step !== 'done' && step <= reached;
  if (!allowed) return <Navigate to={stepPath(enrollment.data.id, reached)} replace />;
  return <>{children(enrollment.data)}</>;
}

export interface FormProblem {
  path: string;
  message: string;
}

/** Every message in a form's errors, nested fields included (`mailing.line1`). */
export function flattenErrors<T extends FieldValues>(
  errors: FieldErrors<T>,
  order: readonly string[] = [],
): FormProblem[] {
  const found: FormProblem[] = [];
  const walk = (node: unknown, path: string) => {
    if (!node || typeof node !== 'object') return;
    const leaf = node as { message?: unknown; type?: unknown };
    // A field error carries `type`; anything else is a group of fields.
    if (typeof leaf.type === 'string' || typeof leaf.message === 'string') {
      if (typeof leaf.message === 'string' && leaf.message)
        found.push({ path, message: leaf.message });
      return;
    }
    for (const [key, child] of Object.entries(node)) walk(child, path ? `${path}.${key}` : key);
  };
  walk(errors, '');
  // In the order of the form, so the list reads top to bottom like the page.
  const rank = (path: string) => {
    const index = order.indexOf(path);
    return index === -1 ? order.length : index;
  };
  return found.sort((a, b) => rank(a.path) - rank(b.path));
}

/**
 * The list of problems at the top of a wizard form. The reference validates on the server and
 * reloads the page with such a list; here each field is marked as well.
 */
export const ProblemList = forwardRef<HTMLDivElement, { problems: FormProblem[] }>(
  function ProblemList({ problems }, ref) {
    if (problems.length === 0) return null;
    return (
      <Alert severity="error" ref={ref} sx={{ mb: 2 }} aria-label="Problems with this form">
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {problems.map((p) => (
            <li key={`${p.path}:${p.message}`}>{p.message}</li>
          ))}
        </Box>
      </Alert>
    );
  },
);

/** Summary line of steps 3 and 4: Enrollment ID, Customer Name, Customer Address, Customer Plan. */
export function OrderSummary({ enrollment }: { enrollment: EnrollmentDto }) {
  const items: Array<[string, string]> = [
    ['Enrollment ID', enrollment.enrollmentId],
    ['Customer Name', customerName(enrollment.applicant)],
    // The reference shows the address in upper case, as its address service returns it.
    ['Customer Address', serviceAddress(enrollment.applicant).toUpperCase()],
    ['Customer Plan', enrollment.plan?.name ?? ''],
  ];
  return (
    <Box
      component="dl"
      aria-label="Order summary"
      sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 3, rowGap: 0.5, m: 0, mb: 2 }}
    >
      {items.map(([label, value]) => (
        <Box key={label} sx={{ display: 'flex', gap: 0.75 }}>
          <Typography component="dt" sx={{ fontWeight: 600 }}>
            {label}:
          </Typography>
          <Typography component="dd" sx={{ m: 0 }}>
            {value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export interface CostLine {
  label: string;
  /** An amount in cents, or a control such as the months select. */
  value: number | ReactNode;
  caption?: string;
  strong?: boolean;
}

/** The cost lines of "Overall Cost Details", in the reference's order. */
export const costLines = (costs: EnrollmentCosts, monthsControl: ReactNode): CostLine[] => [
  { label: 'Total Applicable Plan Cost', value: costs.planCents },
  { label: 'Advance Billing Month', value: monthsControl },
  { label: 'Total Advance Billing Cost', value: costs.advanceCents },
  { label: 'Total Activation Fee', value: costs.activationFeeCents },
  { label: 'Total Handset Cost', value: costs.handsetCents },
  { label: 'Total Regulatory Fee', value: costs.regulatoryFeeCents },
  { label: 'Plan System Discount', value: costs.discountCents },
  { label: 'Plan Additional Service', value: costs.additionalCents },
  { label: 'All Cost Excluding Taxes', value: costs.totalBeforeTaxCents, strong: true },
];

export function CostTable({ lines, label }: { lines: CostLine[]; label: string }) {
  return (
    <Table aria-label={label} sx={{ maxWidth: 560 }}>
      <TableBody>
        {lines.map((line) => (
          <TableRow key={line.label}>
            <TableCell component="th" scope="row" sx={{ fontWeight: line.strong ? 600 : 400 }}>
              {line.label}
              {line.caption ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {line.caption}
                </Typography>
              ) : null}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: line.strong ? 600 : 400, width: 200 }}>
              {typeof line.value === 'number' ? formatCents(line.value) : line.value}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
